import { EVMData } from "@/types/evm";
import { SavedCalculation } from "@/types/cvr";
import { supabase } from "./client";
import { validateEVMData, sanitizeEVMData } from "@/utils/validation/evmValidation";
import { logError } from '@/utils/logger';

export async function fetchEVMCalculations(): Promise<SavedCalculation[]> {
  const { data, error } = await supabase
    .from('evm_calculations')
    .select('*')
    .order('date_saved', { ascending: false });

  if (error) {
    logError('Error fetching calculations:', error);
    throw new Error('Failed to fetch calculations');
  }

  return data || [];
}

export async function saveEVMCalculation(
  projectName: string,
  data: EVMData
): Promise<SavedCalculation> {
  // Validate inputs
  if (!projectName?.trim()) {
    throw new Error('Project name is required');
  }

  const validationError = validateEVMData(data);
  if (validationError) {
    throw new Error(validationError);
  }

  const {
    data: { user },
  } = await supabase.auth.getUser();

  if (!user) {
    throw new Error('You must be signed in to save calculations');
  }

  // Sanitize data before saving
  const sanitizedData = sanitizeEVMData(data);

  const { data: savedData, error } = await supabase
    .from('evm_calculations')
    .insert({
      project_name: projectName.trim(),
      data: sanitizedData,
      user_id: user.id,
    })
    .select('*')
    .single();

  if (error) {
    logError('Error saving calculation:', error);
    throw new Error('Failed to save calculation');
  }

  if (!savedData) {
    throw new Error('No data returned from the server');
  }

  return savedData;
}

export async function deleteEVMCalculation(id: string): Promise<void> {
  if (!id) {
    throw new Error('Calculation ID is required');
  }

  const { error } = await supabase
    .from('evm_calculations')
    .delete()
    .eq('id', id);

  if (error) {
    logError('Error deleting calculation:', error);
    throw new Error('Failed to delete calculation');
  }
}

export async function renameEVMCalculation(
  id: string,
  newName: string
): Promise<void> {
  if (!id) {
    throw new Error('Calculation ID is required');
  }

  if (!newName.trim()) {
    throw new Error('New name is required');
  }

  const { error } = await supabase
    .from('evm_calculations')
    .update({ project_name: newName.trim() })
    .eq('id', id);

  if (error) {
    logError('Error renaming calculation:', error);
    throw new Error('Failed to rename calculation');
  }
}